import { Check, Flag, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type ReviewVerdictStatus = "pending" | "approved" | "flagged";

/** The approve / flag / clear verdict panel shared by every reconciliation
 *  worklist.
 *
 *  The close's review shape is identical everywhere — three mutually exclusive
 *  verdicts, free-text notes, who last touched it, and a Save that's inert until
 *  something actually changed. Only the nouns differ. This owns the draft state
 *  (so a half-typed note isn't lost to a background refetch) and hands the
 *  caller a committed value on save.
 *
 *  Draft state is re-seeded when `itemKey` changes, not on every render: the
 *  worklists poll, and re-seeding on data identity would wipe what the reviewer
 *  is typing. */
export function ReviewVerdict({
  itemKey,
  status,
  statusLabel,
  notes,
  reviewedByName,
  reviewedAt,
  canAct,
  saving,
  presetFlag = false,
  deniedReason,
  flagPlaceholder = "What's wrong? (context for whoever fixes it)",
  approveLabel = "Approve",
  flagLabel = "Flag",
  extra,
  onSave,
  className,
}: {
  /** Identity of the thing under review. Changing it re-seeds the draft. */
  itemKey: string;
  status: ReviewVerdictStatus;
  /** Overrides the badge WORD without touching its tone — R-16 shows an
   *  approved relationship as "Verified". Omit to humanize `status`. */
  statusLabel?: string;
  notes?: string | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  canAct: boolean;
  saving?: boolean;
  /** Opens straight into a flagged draft with the notes box focused — for the
   *  row-level Flag button, where the reviewer has already decided. */
  presetFlag?: boolean;
  /** Shown next to Save when `canAct` is false, e.g. which role owns the step. */
  deniedReason?: string;
  flagPlaceholder?: string;
  /** Verdict button labels — the integrations monitor reads better as
   *  "Expected" / "Real issue" than "Approve" / "Flag". Semantics unchanged. */
  approveLabel?: string;
  flagLabel?: string;
  /** Rendered between the verdict buttons and the notes box — e.g. the
   *  integrations cause picker. State stays in the caller; compose whatever
   *  it holds into your save mutation alongside onSave's payload. */
  extra?: React.ReactNode;
  onSave: (next: { status: ReviewVerdictStatus; notes: string }) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState<ReviewVerdictStatus>(status);
  const [text, setText] = useState(notes ?? "");
  const notesRef = useRef<HTMLTextAreaElement | null>(null);
  const seededKey = useRef<string | null>(null);

  useEffect(() => {
    if (itemKey === seededKey.current) return;
    seededKey.current = itemKey;
    setDraft(presetFlag ? "flagged" : status);
    setText(notes ?? "");
    if (presetFlag) setTimeout(() => notesRef.current?.focus(), 150);
  }, [itemKey, presetFlag, status, notes]);

  const dirty = draft !== status || text !== (notes ?? "");

  return (
    <div className={cn("space-y-3 rounded-md border px-4 py-3", className)}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Review
        </span>
        <StatusBadge value={status} label={statusLabel} />
      </div>

      <div className="flex flex-wrap gap-2">
        <VerdictButton
          active={draft === "approved"}
          activeClass="border-success bg-success-light text-success"
          onClick={() => setDraft("approved")}
          disabled={!canAct}
        >
          <Check className="h-3.5 w-3.5" /> {approveLabel}
        </VerdictButton>
        <VerdictButton
          active={draft === "flagged"}
          activeClass="border-warning bg-warning-light text-warning"
          onClick={() => setDraft("flagged")}
          disabled={!canAct}
        >
          <Flag className="h-3.5 w-3.5" /> {flagLabel}
        </VerdictButton>
        <VerdictButton
          active={draft === "pending"}
          activeClass="border-border bg-muted text-foreground"
          onClick={() => setDraft("pending")}
          disabled={!canAct}
        >
          <RotateCcw className="h-3.5 w-3.5" /> Clear
        </VerdictButton>
      </div>

      {extra}

      <Textarea
        ref={notesRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!canAct}
        rows={3}
        aria-label="Review notes"
        placeholder={draft === "flagged" ? flagPlaceholder : "Optional notes…"}
      />

      {reviewedAt && (
        <p className="text-xs text-muted-foreground">
          Last reviewed by {reviewedByName ?? "—"} on{" "}
          {new Date(reviewedAt).toLocaleString()}.
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button
          onClick={() => onSave({ status: draft, notes: text })}
          disabled={!canAct || !dirty || !!saving}
        >
          {saving ? "Saving…" : "Save review"}
        </Button>
        {!canAct && deniedReason && (
          <span className="text-xs text-muted-foreground">{deniedReason}</span>
        )}
      </div>
    </div>
  );
}

function VerdictButton({
  active,
  activeClass,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        active ? activeClass : "border-border hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
